import type { EscalationData, ResolutionData } from 'api/recoveryAi.types'
import usePrefersReducedMotion from 'hooks/usePrefersReducedMotion'
import type { ChatMessage } from 'hooks/websocket/recoveryChatSource'
import { useEffect, useMemo, useState } from 'react'
import { cannedClosingMessage, cannedUserMessages } from './cannedUserMessages'
import type { PersonaId } from './simulatorTypes'

/**
 * Beat before each customer turn. The conversation is real, so the pacing only
 * has to keep it readable — enough of a pause that the merchant sees the reply
 * land and settle before the next question arrives, rather than the turn
 * snapping in the instant the stream closes.
 */
export const TURN_BEAT_MS = 1200

/**
 * How many times the closing line may be sent once the scripted turns run out.
 *
 * The bound is the point. A live agent can keep asking questions the script
 * never anticipated, and each answer costs a full brain turn on a merchant's
 * first-impression screen — so past this many attempts the scenario is left
 * unanswered on purpose. Two gives the agent a fair chance to conclude after an
 * unforeseen question without ever becoming a loop.
 */
export const MAX_CLOSING_TURNS = 2

type Options = {
  personaId: PersonaId
  /**
   * Identity of the current run of this scenario. Change it to replay the script
   * from turn 0 on the SAME persona.
   *
   * The cursor is otherwise only re-armed by a persona change, so a caller that
   * clears the conversation in place (a brand-voice save restarting the guided
   * phase) would leave the cursor spent: `scriptedTurnDue` is false because the
   * turns are used up, and `closingDue` is false because an empty log has no
   * agent reply to answer — a permanently blank chat. Defaults to 0 so callers
   * that never replay are unchanged.
   */
  runId?: number
  /**
   * Gates SCHEDULING only — the hook is always called (Rules of Hooks). Pass
   * `isCannedLive && ready && !error && !holdGuidedPlayback`: the shell renders
   * SimulatorStatus instead of the engine until `ready`, and the coach tour owns
   * when the conversation may start moving.
   */
  enabled: boolean
  isConnected: boolean
  awaitingResponse: boolean
  currentStreamingMessage: string | null
  isToolRunning: boolean
  resolutionData: ResolutionData | null
  escalationData: EscalationData | null
  messages: ChatMessage[]
  sendMessage: (text: string) => void
}

/**
 * Drives one guided scenario's scripted CUSTOMER turns into the live agent, one
 * at a time, waiting for each reply before asking the next thing.
 *
 * ⚠ NOT a RecoveryChatSource and never a substitute for one — it consumes a live
 * source's state and calls back into that same source's `sendMessage`. It is
 * called unconditionally beside useChatSource, exactly as useTranscriptPlayback
 * is; only the scheduling is branched.
 */
export const useCannedConversation = ({
  personaId,
  runId = 0,
  enabled,
  isConnected,
  awaitingResponse,
  currentStreamingMessage,
  isToolRunning,
  resolutionData,
  escalationData,
  messages,
  sendMessage,
}: Options): void => {
  // WCAG: the beat is the one piece of pacing this hook controls — the rest of
  // the conversation runs at the agent's speed — so a merchant who asked the OS
  // for less motion gets each turn sent the moment it is due instead of watching
  // a staged pause. The scripted transcript honoured the same preference by
  // jumping straight to its finished state (useTranscriptPlayback); there is no
  // finished state to jump to here, so dropping the delay is the equivalent.
  const reducedMotion = usePrefersReducedMotion()
  const beatMs = reducedMotion ? 0 : TURN_BEAT_MS

  // The cursor carries the persona it belongs to, so a Previous/Next is
  // corrected during render rather than by an effect — the same shape
  // useTranscriptPlayback uses, and for the same reason: the shell is not
  // re-keyed on a persona change, so an effect-based reset would let the new
  // scenario inherit the old one's position for a frame.
  const [cursor, setCursor] = useState({
    personaId,
    runId,
    turn: 0,
    closings: 0,
  })
  // A replay (runId) is corrected here for the same reason a persona switch is:
  // during render, so the new run cannot inherit the old one's position for a
  // frame the way an effect-based reset would.
  const onThisRun = cursor.personaId === personaId && cursor.runId === runId
  const turn = onThisRun ? cursor.turn : 0
  // Re-armed by a persona switch or a replay along with `turn`: each run gets its
  // own closing budget, not a share of one running total.
  const closings = onThisRun ? cursor.closings : 0

  // Memoized because it is an effect dep and the effect ARMS A TIMER: a fresh
  // array per render would clear and re-arm the beat on every re-render, and a
  // live socket re-renders the shell often enough to starve the send entirely.
  const turns = useMemo(() => cannedUserMessages(personaId), [personaId])
  const closing = useMemo(() => cannedClosingMessage(), [])

  // `awaitingResponse` clears on the FIRST text_chunk, so on its own it means
  // "the agent started talking", not "the agent finished". Sending on that edge
  // would drop turn 2 on top of the reply to turn 1. All three terms have to be
  // idle before a turn is due.
  const turnSettled =
    isConnected &&
    !awaitingResponse &&
    currentStreamingMessage === null &&
    !isToolRunning

  // The agent owns the outcome now, so it can close the conversation before the
  // script runs out — repeat_complainer escalates by design, and any grant is
  // terminal. Sending past that is a customer message nothing will ever answer.
  const conversationEnded = resolutionData !== null || escalationData !== null

  // Every turn after the first waits for an actual agent reply to be the last
  // thing in the log. `sendMessage` appends the customer's own message locally
  // and synchronously (an optimistic echo — the socket never sends it back), so
  // "messages grew" alone would re-arm on our own send. Reading the role instead
  // is what makes this safe: the moment `turn` reaches `turns.length` the log
  // still ends with that local `user` entry, so neither gate below can fire on
  // a send that has not been answered yet.
  //   context: this leans on `useRecoveryChat.sendMessage` appending the user
  //   message BEFORE it sets `awaitingResponse`. The hook's own tests mock
  //   `sendMessage` as a bare spy and drive `messages` by hand, so they cannot
  //   catch a reorder — see the matching note in useRecoveryChat.ts.
  const lastMessage = messages[messages.length - 1]
  const agentHasReplied = lastMessage?.role === 'ai'

  // The opening turn is the one send that does NOT wait for the agent — there is
  // nothing to reply to yet.
  const scriptedTurnDue = turn < turns.length && (turn === 0 || agentHasReplied)

  // The script is spent but the agent is still talking, so it asked something the
  // two scripted turns did not anticipate. Bounded — see MAX_CLOSING_TURNS.
  const closingDue =
    turn >= turns.length && agentHasReplied && closings < MAX_CLOSING_TURNS

  useEffect(() => {
    if (!enabled || !turnSettled || conversationEnded) return undefined
    if (!scriptedTurnDue && !closingDue) return undefined

    const timer = window.setTimeout(() => {
      if (scriptedTurnDue) {
        sendMessage(turns[turn])
        setCursor({ personaId, runId, turn: turn + 1, closings })
      } else {
        sendMessage(closing)
        setCursor({ personaId, runId, turn, closings: closings + 1 })
      }
    }, beatMs)
    // Covers unmount, a persona switch, the tour hold re-engaging and the
    // conversation going terminal mid-beat — exactly one timer is ever live.
    return () => window.clearTimeout(timer)
  }, [
    enabled,
    turnSettled,
    conversationEnded,
    scriptedTurnDue,
    closingDue,
    beatMs,
    personaId,
    runId,
    turn,
    closings,
    turns,
    closing,
    sendMessage,
  ])
}

export default useCannedConversation
